import React, { useEffect, useMemo, useState } from 'react';
import { collectionGroup, onSnapshot as fbOnSnapshot } from 'firebase/firestore';
import { db, collection, safeOnSnapshot, query, where } from '../firebaseConfig';
import { Player, RoomData, ScheduleRitual } from '../types';
import { canSeeRitual } from './SchedulePopup';
import { expandRitual } from '../utils/expandRitual';

interface HallwayScheduleProps {
  currentUser: Player;
  rooms: Record<string, RoomData>;
  onNavigateToRoom: (roomId: string) => void;
  onOpenFullSchedule: () => void;
}

const txt = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') return val.da || val.en || (Object.values(val).find((v: any) => typeof v === 'string') as string) || '';
  return String(val);
};

const ymd = (d: Date): string =>
  d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

const HallwaySchedule: React.FC<HallwayScheduleProps> = ({ currentUser, rooms, onNavigateToRoom, onOpenFullSchedule }) => {
  const [rituals, setRituals] = useState<any[]>([]);
  const [vacs, setVacs] = useState<any[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(function () { setTick(function (t) { return t + 1; }); }, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const unsub = fbOnSnapshot(collectionGroup(db, 'rituals'), (snap: any) => {
      const out: any[] = [];
      snap.forEach((d: any) => {
        const rid = d.ref.parent.parent ? d.ref.parent.parent.id : '';
        if (rid === '' || rid.indexOf('config_') === 0) return;
        const data: any = { ...d.data(), id: d.id, __roomId: rid };
        if (canSeeRitual(data as ScheduleRitual, currentUser)) out.push(data);
      });
      setRituals(out);
    });
    return () => { try { unsub && unsub(); } catch (e) {} };
  }, [currentUser.id, currentUser.ageGroup, currentUser.role]);

  useEffect(() => {
    const unsub = safeOnSnapshot(query(collection(db, 'school_years'), where('status', '==', 'published')), (snap: any) => {
      const v: any[] = [];
      snap.docs.forEach((d: any) => {
        (((d.data() || {}).vacations) || []).forEach((x: any) => {
          if (x.from && x.to) v.push({ from: x.from, to: x.to, hideLessons: x.hideLessons });
        });
      });
      setVacs(v);
    });
    return () => { try { unsub && unsub(); } catch (e) {} };
  }, []);

  const found = useMemo(() => {
    const now = new Date();
    const today = ymd(now);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const vacRanges = vacs.filter((v: any) => v.hideLessons !== false).map((v: any) => ({ from: v.from, to: v.to }));
    const scan = (days: number): any => {
      const end = new Date(now); end.setDate(end.getDate() + days);
      let ongoing: any = null; let best: any = null;
      for (const r of rituals) {
        if (!rooms[r.__roomId]) continue;
        const occ = expandRitual(r, today, ymd(end), vacRanges, true, false);
        for (const o of occ) {
          const cand: any = { o: o, roomId: r.__roomId };
          if (o.date === today && o.startMinutes <= nowMin && o.endMinutes > nowMin) {
            if (ongoing === null || o.startMinutes < ongoing.o.startMinutes) ongoing = cand;
          } else if (o.date > today || o.startMinutes > nowMin) {
            if (best === null || o.date < best.o.date || (o.date === best.o.date && o.startMinutes < best.o.startMinutes)) best = cand;
          }
        }
      }
      return { ongoing: ongoing, next: best, primary: ongoing || best };
    };
    const r14 = scan(14);
    if (r14.primary) return r14;
    return scan(180);
  }, [rituals, vacs, rooms, tick]);

  const label = (o: any): string => {
    const now = new Date();
    const today = ymd(now);
    const t = new Date(now); t.setDate(t.getDate() + 1);
    const p = o.date.split('-').map(Number);
    const d = new Date(p[0], p[1] - 1, p[2]);
    if (o.date === today) {
      const diff = o.startMinutes - (now.getHours() * 60 + now.getMinutes());
      const spand = o.time + (o.endTime ? ' \u2013 ' + o.endTime : '');
      if (diff <= 1) return 'om et \u00f8jeblik \u00b7 ' + spand;
      if (diff <= 90) return 'om ' + diff + ' min \u00b7 ' + spand;
      return 'I dag ' + spand;
    }
    const spand2 = o.time + (o.endTime ? ' \u2013 ' + o.endTime : '');
    if (o.date === ymd(t)) return 'I morgen ' + spand2;
    const wd = d.toLocaleDateString('da-DK', { weekday: 'long' });
    return wd.charAt(0).toUpperCase() + wd.slice(1) + ' ' + d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) + ' ' + spand2;
  };

  const nowRef = new Date();
  const nowMinNow = nowRef.getHours() * 60 + nowRef.getMinutes();
  const primary: any = found ? found.primary : null;
  const naeste: any = found ? found.next : null;
  const room: any = primary ? rooms[primary.roomId] : null;
  const naesteRum: any = naeste ? rooms[naeste.roomId] : null;
  const isNow = primary ? (primary.o.date === ymd(nowRef) && primary.o.startMinutes <= nowMinNow && primary.o.endMinutes > nowMinNow) : false;

  return (
    <div className="absolute top-4 left-4 z-[80] w-56 md:w-72 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#0a0a1a]/90 border border-yellow-500/30 rounded-2xl shadow-2xl backdrop-blur-md overflow-hidden">
        {primary && room ? (
          <>
          <button onClick={() => onNavigateToRoom(primary.roomId)} className="w-full text-left p-4 hover:bg-yellow-500/10 transition-all">
            <p className="text-[8px] font-black text-yellow-500/70 uppercase tracking-[0.3em] mb-1">{isNow ? 'I gang nu' : 'N\u00e6ste lektion'}</p>
            <p className="text-yellow-500 font-cinzel text-sm leading-tight mb-1">
              {isNow ? primary.o.time + ' \u2013 ' + primary.o.endTime : label(primary.o)}
            </p>
            <p className="text-white text-xs font-bold leading-tight">{txt(primary.o.titleOverride) || txt(primary.o.master.title) || 'Lektion'}</p>
            <p className="text-[10px] text-indigo-200 mt-1 truncate">{'\uD83D\uDCCD'} {txt(room.name)}</p>
          </button>
          {/* Naar noget er i gang, skal eleven ogsaa kunne se hvornaar hun skal
              videre — og til hvilket rum. Ellers skal hun gaette. */}
          {isNow && naeste && naesteRum && (
            <button onClick={() => onNavigateToRoom(naeste.roomId)} className="w-full text-left px-4 py-2 border-t border-white/10 hover:bg-indigo-600/20 transition-all">
              <p className="text-[8px] font-black text-indigo-300/70 uppercase tracking-[0.3em] mb-0.5">Derefter</p>
              <p className="text-indigo-200 text-[11px] font-bold leading-tight">
                {label(naeste.o)} · {txt(naeste.o.titleOverride) || txt(naeste.o.master.title) || 'Lektion'}
              </p>
              <p className="text-[10px] text-indigo-300/70 truncate">{'\uD83D\uDCCD'} {txt(naesteRum.name)}</p>
            </button>
          )}
          </>
        ) : (
          <div className="p-4">
            <p className="text-[8px] font-black text-yellow-500/70 uppercase tracking-[0.3em] mb-1">Dit skema</p>
            <p className="text-white/60 text-xs">Ingen lektioner planlagt</p>
          </div>
        )}
        <button onClick={onOpenFullSchedule} className="w-full border-t border-white/10 py-2 text-[9px] font-black uppercase tracking-widest text-indigo-300 hover:bg-indigo-600 hover:text-white transition-all">
          Se hele mit skema
        </button>
      </div>
    </div>
  );
};

export default HallwaySchedule;
